import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ja } from 'date-fns/locale/ja';
import { Box } from '@mui/material';
import { OptionsBase } from '../../../state/master';
import { format, parse, isValid } from 'date-fns';

export type FreeDatePickerProps = {
  freeOptionsList: OptionsBase[];
  value: Date | string | null;  // string型も受け入れるように変更
  onChange: (newDate: Date | string | null) => void;  // string型も返せるように変更
  outputAsString?: boolean;  // 文字列として出力するかのオプション
  id?: string;  // idプロパティを追加
};

export const FreeDatePicker: React.FC<FreeDatePickerProps> = ({
  freeOptionsList,
  value,
  onChange,
  outputAsString = true,  // デフォルトで文字列として出力
  id,
}) => {
  // 「納期」オプションを抽出
  const optionList = freeOptionsList.filter(opt => opt.label);
  const option = optionList[0];

  if (!option) {
    return (
      <Box
        component="span"
        sx={{ color: 'error.main', padding: '8px' }}
      >
        日付項目設定エラー
      </Box>
    );
  }

  // 文字列をDateオブジェクトに変換する関数
  const parseValue = (val: Date | string | null): Date | null => {
    if (!val) return null;
    
    if (val instanceof Date) {
      return isValid(val) ? val : null;
    }
    
    if (typeof val === 'string') {
      if (val === '') return null;
      
      // 様々な日付フォーマットを試す
      const formats = [
        'yyyy/MM/dd',
        'yyyy-MM-dd',
        'yyyy/M/d',
        'yyyy-M-d',
      ];
      
      for (const fmt of formats) {
        try {
          const parsed = parse(val, fmt, new Date());
          if (isValid(parsed)) {
            return parsed;
          }
        } catch (e) {
          // 次のフォーマットを試す
        }
      }
      
      // ISO形式の文字列として解析を試みる
      const dateFromString = new Date(val);
      return isValid(dateFromString) ? dateFromString : null;
    }
    
    return null;
  };

  // 内部で使用するDate値
  const internalValue = parseValue(value);

  // onChange ハンドラー
  const handleChange = (newDate: Date | null) => {
    if (outputAsString) {
      // 文字列として出力する場合
      if (newDate && isValid(newDate)) {
        const formattedDate = format(newDate, 'yyyy/MM/dd');
        onChange(formattedDate);
      } else {
        onChange(null);
      }
    } else {
      // Dateオブジェクトとして出力する場合
      onChange(newDate);
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={ja}
    >
      <DatePicker
        label={option.label}
        value={internalValue}
        onChange={handleChange}
        format="yyyy/MM/dd"
        // ポッパー内の▽矢印要素を非表示にする
        slotProps={{
          switchViewIcon: {
            sx: { display: 'none' }
          },
          textField: {
            id: id || 'deadline-date-picker',
            error: false,  // エラー状態を制御
          },
          popper: {
            placement: 'bottom-start',
          }
        }}
      />
    </LocalizationProvider>
  );
};